import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { type SignupMode, useAdminSettings, useUpdateAdminSettings } from '@/lib/settings';

const MODE_OPTIONS: { value: SignupMode; title: string; help: string }[] = [
  {
    value: 'open',
    title: 'Open — anyone can sign up',
    help: 'The public sign-up form creates accounts immediately, no admin involvement.',
  },
  {
    value: 'request',
    title: 'Request — admin approval required',
    help: 'Visitors fill a request form. You review the queue and approve or deny each one; approvals send a "set your password" email.',
  },
  {
    value: 'closed',
    title: 'Closed — invitation or admin-create only',
    help: 'No public sign-up at all. Existing users sign in normally; new accounts come from invitations or the admin-create flow.',
  },
];

export function SettingsPanel() {
  const settings = useAdminSettings();
  const update = useUpdateAdminSettings();

  const [mode, setMode] = useState<SignupMode>('closed');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (settings.data) {
      setMode(settings.data.signup_mode);
      setMessage(settings.data.signup_disabled_message ?? '');
    }
  }, [settings.data]);

  const dirty =
    settings.data &&
    (mode !== settings.data.signup_mode ||
      message !== (settings.data.signup_disabled_message ?? ''));

  return (
    <section className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-5">
      <h2 className="text-lg font-semibold">System settings</h2>
      <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
        Controls that apply to the whole instance.
      </p>

      <div className="mt-4 space-y-5">
        <fieldset>
          <legend className="text-sm font-medium text-stone-800 dark:text-stone-200">
            Sign-up policy
          </legend>
          <div className="mt-2 space-y-2">
            {MODE_OPTIONS.map((opt) => (
              <label key={opt.value} className="flex items-start gap-3">
                <input
                  type="radio"
                  name="signup_mode"
                  value={opt.value}
                  checked={mode === opt.value}
                  onChange={() => setMode(opt.value)}
                  className="mt-1 h-4 w-4 border-stone-300 dark:border-stone-700 text-forge-600 focus:ring-forge-500"
                />
                <span>
                  <span className="block text-sm font-medium text-stone-800 dark:text-stone-200">
                    {opt.title}
                  </span>
                  <span className="block text-xs text-stone-500 dark:text-stone-400">
                    {opt.help}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-stone-700 dark:text-stone-300">
            Sign-up message
          </span>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Optional. Shown on the closed sign-up page."
            rows={2}
            className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm focus:border-forge-500 focus:outline-none focus:ring-1 focus:ring-forge-500"
          />
        </label>

        <div className="flex items-center gap-3">
          <Button
            disabled={!dirty || update.isPending}
            onClick={() =>
              update.mutate({
                signup_mode: mode,
                signup_disabled_message: message || null,
              })
            }
          >
            {update.isPending ? 'Saving…' : 'Save settings'}
          </Button>
          {update.isError && (
            <span className="text-sm text-red-600">
              {(update.error as Error).message}
            </span>
          )}
          {update.isSuccess && !dirty && (
            <span className="text-sm text-green-700">Saved.</span>
          )}
        </div>
      </div>
    </section>
  );
}
